/**
 * Cabinet shared helpers — API access, role gating, modal a11y, tiny widgets.
 * Used by CabinetView and the cabinet/* components only.
 */
import React, { useEffect, useRef } from 'react';
import { X, AlertTriangle, RefreshCw, Scale } from 'lucide-react';
import { cx, Button, Badge, Card, CardBody } from '../kit';

export const API_URL = import.meta.env.VITE_API_URL ?? '';
export const PAGE_SIZE = 50;

export const titleize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// role → capability rank (backend enforces regardless; this stops the UI offering
// buttons that can only 403). Unknown/missing role is treated as viewer.
const ROLE_RANK = { viewer: 0, operator: 1, manager: 2, admin: 3 };
export function myRoleRank() {
  try {
    const role = (JSON.parse(localStorage.getItem('userData') || '{}').role || '').toLowerCase();
    return ROLE_RANK[role] ?? 0;
  } catch { return 0; }
}

/**
 * Which sign-in this request belongs to.
 *
 * The staff portal and the office are two different people on one browser: a
 * counter signs in on the phone, an accounts manager signs in at the desk, and
 * for a long while both wrote the same `authToken`, so whoever signed in last
 * silently took over the other's session — an admin would find themselves
 * refused a branch because a counter's token was answering for them.
 *
 * They now keep their own keys, and the path says which one is asking:
 * anything under /branch is the portal, everything else is the office. The
 * fallback to `authToken` keeps a portal already signed in working until its
 * shift token expires.
 */
export function sessionToken() {
  const portal = typeof window !== 'undefined' && window.location?.pathname?.startsWith('/branch');
  if (portal) return localStorage.getItem('branchToken') || localStorage.getItem('authToken');
  return localStorage.getItem('authToken');
}

export function sessionUser() {
  const portal = typeof window !== 'undefined' && window.location?.pathname?.startsWith('/branch');
  try { return JSON.parse(localStorage.getItem(portal ? 'branchUser' : 'userData') || '{}'); }
  catch { return {}; }
}

export async function api(path, method = 'GET', body = null) {
  const token = sessionToken();
  const opts = { method, headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) } };
  if (body) opts.body = JSON.stringify(body);
  const r = await fetch(`${API_URL}${path}`, opts);
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    // FastAPI `detail` is usually a string, but some endpoints (e.g. the M6
    // recipient-typo guard) return an object {message, suggestions:[…]}. Keep
    // Error.message a string always; expose the raw payload on err.detail so
    // callers can render suggestion cards. (Back-compatible: message + status
    // unchanged for every existing caller.)
    const detail = e.detail;
    const msg = typeof detail === 'string' ? detail
      : (detail && (detail.message || detail.msg)) || `HTTP ${r.status}`;
    const err = new Error(msg);
    err.status = r.status;
    err.detail = detail;
    throw err;
  }
  return r.json();
}

// Authenticated binary fetch (render pages, file download) — a bare <img src> or
// <a href> can't carry the bearer token, so everything binary goes through blobs.
export async function apiBlob(path) {
  const token = sessionToken();
  const r = await fetch(`${API_URL}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!r.ok) { const err = new Error(`HTTP ${r.status}`); err.status = r.status; throw err; }
  return r.blob();
}

// Authenticated multipart upload — same error contract as `api` (string OR
// object `detail`, exposed on err.detail). Do NOT set Content-Type: the browser
// must add the multipart boundary itself.
/** A file from the API by any method — zip, csv, xlsx, pdf — with the token the JSON calls use.
 *  Returns the blob and the filename the server named. (apiBlob above stays GET-only for images.) */
export async function apiFile(path, method = 'GET', body = null) {
  const token = sessionToken();
  const opts = { method, headers: { ...(body ? { 'Content-Type': 'application/json' } : {}), ...(token && { Authorization: `Bearer ${token}` }) } };
  if (body) opts.body = JSON.stringify(body);
  const r = await fetch(`${API_URL}${path}`, opts);
  if (!r.ok) {
    let msg = `HTTP ${r.status}`;
    try { const j = await r.json(); msg = (typeof j.detail === 'string' && j.detail) || j.error || msg; } catch {}
    const err = new Error(msg); err.status = r.status; throw err;
  }
  const m = (r.headers.get('Content-Disposition') || '').match(/filename="?([^";]+)"?/);
  return { blob: await r.blob(), filename: m ? m[1] : '', headers: r.headers };
}

export function saveBlob(blob, filename) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob); a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

export async function apiUpload(path, formData, method = 'POST') {
  const token = sessionToken();
  const r = await fetch(`${API_URL}${path}`, {
    method,
    headers: { ...(token && { Authorization: `Bearer ${token}` }) },
    body: formData,
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    const detail = e.detail;
    const msg = typeof detail === 'string' ? detail
      : (detail && (detail.message || detail.msg)) || `HTTP ${r.status}`;
    const err = new Error(msg);
    err.status = r.status;
    err.detail = detail;
    throw err;
  }
  return r.json();
}

// ── Modal / drawer a11y: Esc closes, focus trapped, background scroll locked ──
// A stack of open layers lets modals nest (permissions → groups): Esc and the
// Tab trap act on the TOPMOST layer only, so closing peels one layer at a time.
const modalStack = [];
export function useModalA11y(onClose) {
  const ref = useRef(null);
  const closeRef = useRef(onClose);
  useEffect(() => { closeRef.current = onClose; });
  useEffect(() => {
    const node = ref.current;
    const token = {};
    modalStack.push(token);
    const isTop = () => modalStack[modalStack.length - 1] === token;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const prevFocus = document.activeElement;
    const focusables = () => (node
      ? Array.from(node.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
        .filter((el) => !el.disabled && el.offsetParent !== null)
      : []);
    (focusables()[0] || node)?.focus?.();
    const onKey = (e) => {
      if (!isTop()) return;
      if (e.key === 'Escape') { e.stopPropagation(); closeRef.current?.(); return; }
      if (e.key === 'Tab' && node) {
        const els = focusables();
        if (els.length === 0) { e.preventDefault(); return; }
        const first = els[0]; const last = els[els.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      const i = modalStack.indexOf(token);
      if (i !== -1) modalStack.splice(i, 1);
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prevOverflow;
      prevFocus?.focus?.();
    };
  }, []);
  return ref;
}

export function ModalShell({ onClose, title, icon: Icon, iconClass = 'text-indigo-500', children, footer, maxW = 'max-w-md' }) {
  const ref = useModalA11y(onClose);
  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm" onMouseDown={onClose}>
      <div
        ref={ref} role="dialog" aria-modal="true" tabIndex={-1}
        className={cx('w-full bg-white rounded-2xl shadow-xl border border-gray-200 max-h-[88vh] flex flex-col focus:outline-none', maxW)}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900 inline-flex items-center gap-2 min-w-0">
            {Icon && <Icon size={17} className={cx('shrink-0', iconClass)} />}
            <span className="truncate">{title}</span>
          </h2>
          <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-700 shrink-0 ml-3"><X size={18} /></button>
        </div>
        <div className="p-5 overflow-y-auto">{children}</div>
        {footer && <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">{footer}</div>}
      </div>
    </div>
  );
}

// ── Small shared widgets ──────────────────────────────────────────────────────
export function ErrorCard({ message, onRetry }) {
  return (
    <Card className="border-rose-200">
      <CardBody className="flex items-center gap-3">
        <AlertTriangle size={18} className="text-rose-500 shrink-0" />
        <div className="flex-1 text-sm text-rose-700">{message || 'Something went wrong.'}</div>
        {onRetry && (
          <Button size="sm" variant="secondary" onClick={onRetry}><RefreshCw size={13} /> Retry</Button>
        )}
      </CardBody>
    </Card>
  );
}

export function LoadMore({ shown, total, loading, onClick }) {
  if (!(Number(total) > Number(shown))) return null;
  return (
    <div className="flex justify-center pt-1">
      <Button variant="secondary" size="sm" loading={loading} onClick={onClick}>
        Load more ({shown} of {total})
      </Button>
    </div>
  );
}

const retentionPast = (d) => d && d <= new Date().toISOString().slice(0, 10);

export function RetentionBadge({ doc }) {
  if (doc.legal_hold) return <Badge tone="indigo"><Scale size={11} className="inline mr-1" />Legal hold</Badge>;
  if (doc.status === 'archived') return <Badge tone="gray">Archived</Badge>;
  if (!doc.retention_until) return <Badge tone="gray">No schedule</Badge>;
  return (
    <Badge tone={retentionPast(doc.retention_until) ? 'red' : 'green'}>
      {retentionPast(doc.retention_until) ? 'Retention expired' : `Keep until ${doc.retention_until}`}
    </Badge>
  );
}
